import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { DownOutlined } from '@ant-design/icons';
import { Checkbox, Select, TimePicker } from 'antd';
import type { TimePickerProps } from 'antd';
import classNames from 'classnames';
import moment, { type Moment } from 'moment';

import { COLACION_OPTIONS, DEFAULT_ACTIVE_DAY } from '../constants';
import type { ActiveDayValue, Recurrence, ValidationMessage } from '../types';

const { Option } = Select;

interface BreakFieldsState {
  breakTime?: number | null;
  startBreakTime?: Moment | string | null;
  isActive?: boolean;
}

interface ActiveDayProps {
  direction?: 'horizontal' | 'vertical';
  includeActiveDay?: boolean;
  defaultActiveDay?: boolean;
  value?: ActiveDayValue;
  onChange: (value: ActiveDayValue) => void;
  disabled?: boolean;
  className?: string;
  warnings?: {
    rangeHours?: ValidationMessage[];
  };
  errors?: {
    breakTime?: ValidationMessage[];
    rangeHours?: ValidationMessage[];
  };
}

/**
 * When `breakTime` is set (any duration, not "none"), `startBreakTime` is required.
 * @returns Error message or null if valid
 */
export function validateActiveDayBreakFields(
  dayState: BreakFieldsState | null | undefined,
  intl: IntlShape,
): string | null {
  if (!dayState) return null;
  const { breakTime, startBreakTime, isActive } = dayState;
  if (isActive === false) return null;
  if (breakTime == null) return null;
  if (startBreakTime == null || startBreakTime === undefined) {
    return intl.formatMessage({ id: 'form.required.msg' });
  }
  if (moment.isMoment(startBreakTime) && !startBreakTime.isValid()) {
    return intl.formatMessage({ id: 'form.required.msg' });
  }
  return null;
}

export const createRecurrenceStartBreakRule = (intl: IntlShape) => ({
  validator: (_: unknown, value: BreakFieldsState) => {
    const msg = validateActiveDayBreakFields(value, intl);
    return msg ? Promise.reject(new Error(msg)) : Promise.resolve();
  },
});

export const createRecurrenceWeeksStartBreakRule = (intl: IntlShape) => ({
  validator: (_: unknown, value?: Recurrence | null) => {
    if (!value?.weeks?.length) return Promise.resolve();
    for (const week of value.weeks) {
      if (!week?.days) continue;
      for (const dayState of Object.values(week.days)) {
        const msg = validateActiveDayBreakFields(dayState as BreakFieldsState, intl);
        if (msg) return Promise.reject(new Error(msg));
      }
    }
    return Promise.resolve();
  },
});

const DEFAULT_ACTIVE = true;
const ActiveDay = ({
  direction = 'vertical', // horizontal | vertical
  includeActiveDay = false,
  defaultActiveDay = DEFAULT_ACTIVE,
  value,
  onChange,
  disabled = false,
  warnings = {
    rangeHours: [],
  },
  errors = {
    breakTime: [],
  },
}: ActiveDayProps) => {
  const intl = useIntl();
  const [formData, setFormData] = useState<ActiveDayValue>({
    ...DEFAULT_ACTIVE_DAY,
    isActive: defaultActiveDay,
  });
  const [error, setError] = useState<{ startBreakTime: string | null }>({
    startBreakTime: null,
  });

  const isHorizontal = direction === 'horizontal';

  const validateStartBreakWhenBreakSelected = (
    breakTime: number | null | undefined,
    startBreakTime: Moment | null | undefined,
    isActive: boolean | undefined,
  ) => validateActiveDayBreakFields({ breakTime, startBreakTime, isActive }, intl);

  const handleTimeRangeChange = React.useCallback(
    (times: [Moment | null, Moment | null] | null) => {
      if (times && times.length === 2) {
        const newFormData: ActiveDayValue = {
          ...formData,
          startTime: times[0],
          endTime: times[1],
          startBreakTime: null,
        };
        setError({
          startBreakTime: validateStartBreakWhenBreakSelected(
            newFormData.breakTime,
            newFormData.startBreakTime,
            newFormData.isActive,
          ),
        });
        setFormData(newFormData);
        onChange(newFormData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.startTime, formData.endTime, formData.breakTime, formData.isActive, onChange],
  );

  const handleBreakTimeChange = (breakValue: number | null) => {
    const newFormData: ActiveDayValue = {
      ...formData,
      breakTime: breakValue,
      startBreakTime: breakValue == null ? null : formData.startBreakTime,
    };
    setError({
      startBreakTime: validateStartBreakWhenBreakSelected(
        newFormData.breakTime,
        newFormData.startBreakTime,
        newFormData.isActive,
      ),
    });
    setFormData(newFormData);
    onChange(newFormData);
  };

  const handleStartBreakTimeChange = (time: Moment | null) => {
    setError({
      startBreakTime: validateStartBreakWhenBreakSelected(
        formData.breakTime,
        time,
        formData.isActive,
      ),
    });
    const newFormData: ActiveDayValue = {
      ...formData,
      startBreakTime: time,
    };
    setFormData(newFormData);
    onChange(newFormData);
  };

  const handleActiveDayChange = (e: { target: { checked: boolean } }) => {
    const newFormData: ActiveDayValue = {
      ...formData,
      isActive: e.target.checked,
    };
    setError({
      startBreakTime: validateStartBreakWhenBreakSelected(
        newFormData.breakTime,
        newFormData.startBreakTime,
        newFormData.isActive,
      ),
    });
    setFormData(newFormData);
    onChange(newFormData);
  };
  /**
   * Returns the first available time.
   * If the start time is valid, returns it.
   * If the start time is not valid, returns the first available time
   * (the current time if it's within the range, or the start time otherwise).
   * @returns The first available time.
   */
  const getFirstAvailableTime = (): Moment => {
    if (!formData.startTime || !formData.endTime) return moment('08:00', 'HH:mm');

    // Returns the start time if it's valid
    if (formData.startTime.isValid()) {
      return formData.startTime;
    }

    // If the start time is not valid, finds the first available time
    const now = moment();
    const startHour = formData.startTime.hour();
    const endHour = formData.endTime.hour();

    if (now.hour() >= startHour && now.hour() <= endHour) {
      return now; // The current time is within the range, so return it
    }

    // If the current time is not within the range, return the start time
    return formData.startTime;
  };

  // Update internal state when value prop changes
  React.useEffect(() => {
    if (value) {
      setFormData(value);
      setError({ startBreakTime: validateActiveDayBreakFields(value, intl) });
    }
  }, [value, intl]);

  const startBreakTimePickerProps = {
    className: 'gx-w-100',
    format: 'HH:mm' as const,
    value: formData.startBreakTime,
    onChange: handleStartBreakTimeChange,
    disabled: formData.breakTime == null || disabled,
    status: (error.startBreakTime ? 'error' : undefined) as TimePickerProps['status'],
    required: formData.breakTime != null,
    placeholder: intl.formatMessage({ id: 'shifts.start.break' }),
    showNow: false,
    defaultOpenValue: getFirstAvailableTime(),
    disabledTime: () => {
      const isDisabled = (hour: number, minute?: number) => {
        if (!formData.startTime || !formData.endTime) return false;

        const startHour = formData.startTime.hour();
        const endHour = formData.endTime.hour();
        const startMinute = formData.startTime.minute();
        const endMinute = formData.endTime.minute();

        if (hour === startHour) return minute < startMinute;

        if (hour === endHour) return minute > endMinute;

        return hour < startHour || hour > endHour;
      };

      return {
        disabledHours: () =>
          Array.from({ length: 24 }, (_, i) => (isDisabled(i) ? i : null)).filter(Number.isInteger),
        disabledMinutes: (selectedHour: number) =>
          Array.from({ length: 60 }, (_, i) => (isDisabled(selectedHour, i) ? i : null)).filter(
            Number.isInteger,
          ),
      };
    },
  } as TimePickerProps & { required?: boolean };

  return (
    <div
      className={classNames(
        direction === 'horizontal'
          ? 'gx-flex-row gx-guarnic-gap-2'
          : 'gx-flex-column gx-guarnic-gap-2',
      )}
    >
      {includeActiveDay && (
        <div>
          <Checkbox
            checked={formData.isActive}
            onChange={handleActiveDayChange}
            className="gx-w-100"
          >
            Dia activo
          </Checkbox>
        </div>
      )}
      {formData.isActive && (
        <>
          <div className={isHorizontal ? 'gx-w-25' : 'gx-w-100'}>
            <TimePicker.RangePicker
              className="gx-w-100"
              disabled={disabled}
              value={[formData.startTime, formData.endTime]}
              onChange={handleTimeRangeChange}
              onOk={handleTimeRangeChange}
              format="HH:mm"
              getPopupContainer={trigger => trigger.parentNode as HTMLElement}
            />
            {warnings?.rangeHours &&
              warnings?.rangeHours?.map(warning => (
                <div key={warning?.key} className="ant-form-item-explain-warning gx-mt-2">
                  {warning?.message}
                </div>
              ))}

            {errors?.rangeHours &&
              errors?.rangeHours?.map(rangeError => (
                <div key={rangeError?.key} className="ant-form-item-explain-error gx-mt-2">
                  {rangeError?.message}
                </div>
              ))}
          </div>
          <div className={isHorizontal ? 'gx-w-25' : 'gx-w-100'}>
            <Select
              className="gx-w-100"
              disabled={disabled}
              value={formData.breakTime}
              onChange={handleBreakTimeChange}
              suffixIcon={<DownOutlined style={{ color: '#bbb' }} />}
              size="large"
            >
              {COLACION_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            {errors.breakTime &&
              errors.breakTime.map(e => (
                <div key={e.key} className="ant-form-item-explain-error gx-mt-2">
                  {e.message}
                </div>
              ))}
          </div>
          <div className={isHorizontal ? 'gx-w-25' : 'gx-w-100'}>
            <TimePicker {...startBreakTimePickerProps} />
          </div>
        </>
      )}
    </div>
  );
};

export default ActiveDay;
